import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  TextField
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useConfig } from '@revenuewell/logic-config';
import { useNotification } from '@revenuewell/logic-notification';
import { useOidcWithAdmin } from '@revenuewell/logic-oidc';
import { usePatientProfile } from 'apps/wrapper/src/hooks/use-patient-profile';
import { useEventBus } from 'apps/wrapper/src/hooks/use-event-bus';
import { SocialMediaService } from 'apps/wrapper/src/services/social-media/social-media-service';
import { SocialMediaPost } from 'apps/wrapper/src/services/social-media/types/social-media-post';
import moment from 'moment';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Review } from '../../../../../services/patient-feedback-service/patient-feedback-service-types';
import { Card, CardTitle } from '../../../card/card.style';
import {
  CloseDialogButton,
  Dialog,
  DialogContent,
  DialogContentWrapper,
  DialogTitle
} from '../../../dialog/dialog.styles';
import { ImageLibraryDialog } from '../../../image-library/image-library';
import { PreviewImageDialog } from '../../../image-library/preview-image/preview-image';
import { SocialMediaType } from '../../types/social-media-type';
import { Hr } from '../dialog.styles';
import {
  PostEditor,
  PostEditorControls,
  PostEditorCounter,
  PostEditorDescription,
  PostEditorSection,
  PostEditorWrapper,
  ScheduleSection,
  ScheduleTime,
  ScheduleTimeBlock,
  StyledStaticDatePicker,
  ToolbarSection
} from './compose-social-media-post.styles';
import { generateTimeOptions } from './generate-time-options';

const FACEBOOK_POST_MAX_LENGTH = 5000;
const TWITTER_POST_MAX_LENGTH = 140;
const URL_REGEX = /https?:\/\/[^\s]+/g;
const ALREADY_SHORT_RE = /rwl\.io|bit\.ly/i;

const shortenLinksButtonSx = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  width: '100%',
  fontSize: '11px',
  fontWeight: 500,
  py: 0.5,
  minHeight: 'unset',
  lineHeight: 1.5,
  color: '#44546F',
  backgroundColor: 'rgba(0,0,0,0.05)',
  borderRadius: '0 0 4px 4px',
  textAlign: 'center',
  justifyContent: 'center',
  border: 'none',
  '&:hover': { backgroundColor: 'rgba(0,0,0,0.09)' }
} as const;

export interface ComposeSocialMediaPrefill {
  message?: string;
  isFacebookPost?: boolean;
  isTwitterPost?: boolean;
  pictureUrlList?: string | null;
  scheduledDate?: Date | null;
  parentPostId?: number;
  postId?: number;
  libraryPostId?: number;
  linkUrl?: string | null;
  linkTitle?: string | null;
  linkDescription?: string | null;
  linkThumbUrl?: string | null;
}

export interface ComposeSocialMediaPostProps {
  open: boolean;
  review: Review | null;
  socialMediaType: SocialMediaType;
  onClose?: () => void;
  onPostSend?: () => Promise<void>;
  locationId?: number;
  prefill?: ComposeSocialMediaPrefill;
  mode?: 'compose' | 'replicate' | 'edit';
}

const ComposeSocialMediaPostImpl: FC<ComposeSocialMediaPostProps & { effectiveLocationId: number | undefined }> = (props) => {
  const { open, review, socialMediaType, onPostSend, onClose, prefill, mode = 'compose', effectiveLocationId } = props;
  const { config } = useConfig();
  const { showError, showSuccess } = useNotification();
  const { oidcService } = useOidcWithAdmin();
  const { publishToIframes } = useEventBus();
  const timeOptions = generateTimeOptions();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingAction, setLoadingAction] = useState<'schedule' | 'postNow' | null>(null);
  const [isScheduleView, setIsScheduleView] = useState<boolean>(false);
  const [postMaxLength, setPostMaxLength] = useState<number>(TWITTER_POST_MAX_LENGTH);
  const [postText, setPostText] = useState<string>('');
  const [selectedLink, setSelectedLink] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date(new Date().setDate(new Date().getDate() + 1)));
  const [selectedTime, setSelectedTime] = useState<Date>(timeOptions[9]);
  const [imageLibraryOpen, setImageLibraryOpen] = useState<boolean>(false);
  const [previewImageDialogOpen, setPreviewImageDialogOpen] = useState<boolean>(false);
  const [attachedImageUrl, setAttachedImageUrl] = useState<string>();
  const [linkMeta, setLinkMeta] = useState<{ url?: string | null; title?: string | null; description?: string | null; thumbUrl?: string | null } | null>(null);
  const socialMediaService = useMemo(() => new SocialMediaService(config, oidcService), [config, oidcService]);
  const [linkMetaLoading, setLinkMetaLoading] = useState<boolean>(false);
  const [isShorteningLinks, setIsShorteningLinks] = useState<boolean>(false);
  const isAutoFetchEnabled = useRef<boolean>(false);
  const linkPreviewDismissed = useRef<boolean>(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevFirstUrl = useRef<string | null>(null);

  useEffect(() => {
    if (open) {
      if (prefill) {
        setPostText(prefill.message ?? '');
        if (prefill.scheduledDate) {
          const scheduledDate = new Date(prefill.scheduledDate);
          setSelectedDate(scheduledDate);
          // Snap to the closest hourly time option
          const prefillHour = scheduledDate.getHours();
          const matchingTime = timeOptions.find(t => t.getHours() === prefillHour) ?? timeOptions[9];
          setSelectedTime(matchingTime);
        }
        if (prefill.pictureUrlList) {
          setAttachedImageUrl(prefill.pictureUrlList);
        }
        if (prefill.linkUrl || prefill.linkTitle || prefill.linkDescription || prefill.linkThumbUrl) {
          setLinkMeta({ url: prefill.linkUrl, title: prefill.linkTitle, description: prefill.linkDescription, thumbUrl: prefill.linkThumbUrl });
        } else {
          setLinkMeta(null);
        }
        setIsScheduleView(mode === 'edit');
      } else if (review) {
        const initialText = `${review.title ?? 'Review'} - by ${review.userName ?? ''}\n\n${
          review.text ?? ''
        }\n\nSee more of our patient reviews here: ${review.patientReviewUrl ?? ''}`;
        setPostText(initialText);
      } else {
        setPostText('');
      }
      setPostMaxLength(socialMediaType == 'Facebook' ? FACEBOOK_POST_MAX_LENGTH : TWITTER_POST_MAX_LENGTH);
    } else {
      setIsScheduleView(false);
      setPostText('');
      setAttachedImageUrl(undefined);
      setLinkMeta(null);
      isAutoFetchEnabled.current = false;
      linkPreviewDismissed.current = false;
      prevFirstUrl.current = null;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    }
  }, [open, prefill, review]);

  const isFacebookPost = socialMediaType === 'Facebook';

  useEffect(() => {
    if (!isFacebookPost || !effectiveLocationId || !isAutoFetchEnabled.current || attachedImageUrl) return;

    const urls = postText.match(URL_REGEX) ?? [];
    const firstUrl = urls[0] ?? null;

    if (firstUrl !== prevFirstUrl.current) {
      linkPreviewDismissed.current = false;
      prevFirstUrl.current = firstUrl;
    }

    if (!firstUrl || linkPreviewDismissed.current) {
      if (!firstUrl) setLinkMeta(null);
      return;
    }

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(async () => {
      setLinkMetaLoading(true);
      try {
        const info = await socialMediaService.getWebPageInfo(effectiveLocationId, firstUrl);
        if (!linkPreviewDismissed.current) {
          setLinkMeta(info ? { url: info.url, title: info.title, description: info.description, thumbUrl: info.imageUrl } : null);
        }
      } catch {
        setLinkMeta(null);
      } finally {
        setLinkMetaLoading(false);
      }
    }, 500);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [postText]);

  const longLinks = (postText.match(URL_REGEX) ?? []).filter(url => !ALREADY_SHORT_RE.test(url));
  const hasLongLinks = longLinks.length > 0;

  const handleShortenLinks = async () => {
    if (!effectiveLocationId) return;
    setIsShorteningLinks(true);
    try {
      const results = await socialMediaService.shortenLinks(effectiveLocationId, longLinks);
      setPostText(prev => {
        let updated = prev;
        for (const { original, shortened } of results) {
          updated = updated.split(original).join(shortened);
        }
        return updated;
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsShorteningLinks(false);
    }
  };

  const formatDateToTimeString = (date: Date): string => {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const handleLinkSelect = (event: SelectChangeEvent<string>) => {
    const mergeField = event.target.value;

    if (mergeField.length < postMaxLength - postText.length) {
      setPostText((prev) => prev + event.target.value);
    }

    setSelectedLink(event.target.value);
  };

  const handleScheduleClick = async () => {
    if (isScheduleView) {
      await createSocialMediaPost('schedule');
    } else {
      setIsScheduleView(true);
    }
  };

  const createSocialMediaPost = async (action: 'schedule' | 'postNow' = 'postNow') => {
    if (!effectiveLocationId) return;

    try {
      setIsLoading(true);
      setLoadingAction(action);

      const isImmediate = !isScheduleView;

      let scheduleDate: Date | undefined;
      if (!isImmediate) {
        scheduleDate = moment(selectedDate)
          .hours(selectedTime.getHours())
          .minutes(selectedTime.getMinutes())
          .seconds(0)
          .milliseconds(0)
          .toDate();
      }

      const post = {
        isFacebookPost: socialMediaType == 'Facebook',
        isTwitterPost: socialMediaType == 'Twitter',
        postTiming: isScheduleView ? 'Scheduled' : 'Immediate',
        message: postText,
        scheduledDate: scheduleDate,
        pictureUrlList: attachedImageUrl ? attachedImageUrl : null,
        parentPostId: prefill?.parentPostId,
        libraryPostId: prefill?.libraryPostId,
        linkUrl: linkMeta?.url ?? undefined,
        linkTitle: linkMeta?.title ?? undefined,
        linkDescription: linkMeta?.description ?? undefined,
        linkThumbUrl: linkMeta?.thumbUrl ?? undefined,
      } as SocialMediaPost;

      if (mode === 'edit' && prefill?.postId != null) {
        if (action === 'postNow') {
          await socialMediaService.createSocialMediaPost(effectiveLocationId, { ...post, parentPostId: prefill.postId });
        } else {
          await socialMediaService.updateScheduledPost(effectiveLocationId, prefill.postId, post);
        }
      } else {
        await socialMediaService.createSocialMediaPost(effectiveLocationId, post);
      }

      publishToIframes({ messageType: 'socialMediaPostSent', data: { locationId: effectiveLocationId, postTiming: isScheduleView ? 'Scheduled' : 'Immediate' } });
      showSuccess('Post has been sent.');
      onPostSend && onPostSend();
    } catch (error) {
      console.error(error);
      showError('Unable to create a post.');
      onClose && onClose();
    } finally {
      setIsLoading(false);
      setLoadingAction(null);
    }
  };

  const handleAttachFromLibrary = async () => {
    setImageLibraryOpen(true);
  };

  return (
    <>
      <Dialog open={open} maxWidth={false}>
        <DialogContentWrapper>
          <DialogTitle sx={{ cursor: 'default' }}>
            {review?.userName}
            <CloseDialogButton onClick={onClose} />
          </DialogTitle>
          <DialogContent sx={{ width: '720px', padding: '40px 20px 0 20px' }}>
            <Card>
              <CardContent style={{ paddingBottom: 0 }}>
                <CardTitle>Compose a Post</CardTitle>
                <Hr />
                <PostEditorSection>
                  <PostEditorWrapper>
                    <PostEditorDescription>Write what you would like to post</PostEditorDescription>
                    <PostEditor>
                      <Box sx={{ position: 'relative', flex: 1 }}>
                        <TextField
                          multiline
                          rows={6}
                          fullWidth
                          value={postText}
                          onChange={(e) => {
                            isAutoFetchEnabled.current = true;
                            setPostText(e.target.value);
                          }}
                          InputProps={{
                            style: { color: '#1C3D64' }
                          }}
                          inputProps={{
                            maxLength: postMaxLength
                          }}
                        />
                        {hasLongLinks && (
                          <LoadingButton
                            loading={isShorteningLinks}
                            variant="text"
                            size="small"
                            onClick={handleShortenLinks}
                            sx={shortenLinksButtonSx}
                          >
                            Shorten Links
                          </LoadingButton>
                        )}
                      </Box>
                      <PostEditorControls>
                        <FormControl sx={{ minWidth: 210, margin: '0 0 0 auto' }} size="small">
                          <InputLabel>Insert Link</InputLabel>
                          <Select
                            label="Insert Link"
                            value={selectedLink}
                            renderValue={() => 'Insert Link'}
                            onChange={handleLinkSelect}
                            inputProps={{
                              style: { color: '#1C3D64' }
                            }}
                          >
                            <MenuItem value={'{Request Appointment}'}>Request Appointment</MenuItem>
                            <MenuItem value={'{Write a Review}'}>Write a Review</MenuItem>
                          </Select>
                        </FormControl>
                        {!attachedImageUrl && !linkMeta && !linkMetaLoading && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleAttachFromLibrary}
                            sx={{ marginTop: '10px' }}
                            disableElevation
                          >
                            Attach Image
                          </Button>
                        )}
                        {attachedImageUrl && (
                          <Box sx={{ display: 'flex', flexDirection: 'row', margin: '10px auto 0 auto' }}>
                            <Button variant="contained" size="small" onClick={handleAttachFromLibrary} disableElevation>
                              Change Image
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => setPreviewImageDialogOpen(true)}
                              sx={{ marginLeft: '10px' }}
                              disableElevation
                            >
                              Preview
                            </Button>
                          </Box>
                        )}
                        <PostEditorCounter>
                          <Box sx={{ fontSize: '24px', textAlign: 'center' }}>{postMaxLength - postText.length}</Box>
                          <Box sx={{ fontSize: '14px', marginTop: '4px' }}>characters remaining</Box>
                        </PostEditorCounter>
                      </PostEditorControls>
                    </PostEditor>
                  </PostEditorWrapper>
                </PostEditorSection>
                <Hr />
                {isFacebookPost && linkMetaLoading && !attachedImageUrl && (
                  <Box sx={{ mt: 2, mb: 2, border: '1px solid #E8ECF0', borderRadius: 1, overflow: 'hidden', display: 'flex', backgroundColor: '#F8FAFC', p: 1.5 }}>
                    <Skeleton variant="rectangular" width={84} height={84} sx={{ flexShrink: 0, mr: 1.5 }} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="60%" height={20} sx={{ mb: 0.5 }} />
                      <Skeleton width="90%" height={16} />
                      <Skeleton width="75%" height={16} />
                    </Box>
                  </Box>
                )}
                {linkMeta && !attachedImageUrl && (
                  <Box sx={{ mt: 2, mb: 2, border: '1px solid #E8ECF0', borderRadius: 1, overflow: 'hidden', display: 'flex', backgroundColor: '#F8FAFC' }}>
                    {linkMeta.thumbUrl && (
                      <Box component="img" src={linkMeta.thumbUrl} alt={linkMeta.title ?? ''} sx={{ width: 84, height: 84, objectFit: 'cover', flexShrink: 0 }} />
                    )}
                    <Box sx={{ p: 1.5, flex: 1, minWidth: 0 }}>
                      {linkMeta.title && (
                        <Box
                          component="a"
                          href={linkMeta.url ?? undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ fontWeight: 600, fontSize: 13, color: '#1E293B', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {linkMeta.title}
                        </Box>
                      )}
                      {linkMeta.description && (
                        <Box sx={{ fontSize: 12, color: '#64748B', lineHeight: 1.5 }}>
                          {linkMeta.description}
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ p: 1, flexShrink: 0 }}>
                      <Button size="small" variant="text" color="error" onClick={() => {
                        linkPreviewDismissed.current = true;
                        setLinkMeta(null);
                      }}>
                        Remove
                      </Button>
                    </Box>
                  </Box>
                )}
                {isScheduleView && (
                  <>
                    <ScheduleSection>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <StyledStaticDatePicker
                          value={selectedDate}
                          views={['day']}
                          disablePast={true}
                          showToolbar={false}
                          renderInput={(params: any) => <TextField {...params} />}
                          onChange={(value: any) => setSelectedDate(new Date(value))}
                          components={{
                            ActionBar: () => null
                          }}
                        />
                      </LocalizationProvider>
                      <ScheduleTimeBlock>
                        <FormControl fullWidth size="small">
                          <InputLabel>Select Time</InputLabel>
                          <Select
                            value={selectedTime ? selectedTime.toISOString() : ''}
                            onChange={(event) => setSelectedTime(new Date(event.target.value))}
                            label="Select Time"
                            renderValue={(value) =>
                              value ? formatDateToTimeString(new Date(value as string)) : 'Select Time'
                            }
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  maxHeight: 250
                                }
                              }
                            }}
                            inputProps={{
                              style: { color: '#1C3D64' }
                            }}
                            sx={{
                              width: '180px'
                            }}
                          >
                            {generateTimeOptions().map((date: Date, index: number) => (
                              <MenuItem key={index} value={date.toISOString()}>
                                {formatDateToTimeString(date)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <ScheduleTime>
                          This post will be published at <span>{moment(selectedTime).format('h:mmA')}</span> on{' '}
                          <span>{moment(selectedDate).format('dddd, MMMM D, YYYY')}</span>
                        </ScheduleTime>
                      </ScheduleTimeBlock>
                    </ScheduleSection>
                    <Hr />
                  </>
                )}
                <ToolbarSection>
                  <Box sx={{ display: 'flex', marginLeft: 'auto' }}>
                    <Button variant="text" onClick={() => onClose && onClose()} color="error">
                      Close
                    </Button>
                    {isScheduleView ? (
                      <>
                        <Button
                          variant="text"
                          onClick={() => setIsScheduleView(false)}
                          sx={{ marginLeft: '16px' }}
                          color="warning"
                        >
                          Cancel Schedule
                        </Button>
                        <LoadingButton
                          loading={loadingAction === 'schedule'}
                          variant="contained"
                          onClick={handleScheduleClick}
                          sx={{ marginLeft: '16px' }}
                          disabled={isLoading || postText.length === 0}
                          disableElevation
                        >
                          Schedule
                        </LoadingButton>
                      </>
                    ) : (
                      <>
                        <LoadingButton
                          loading={loadingAction === 'schedule'}
                          variant="contained"
                          onClick={handleScheduleClick}
                          sx={{ marginLeft: '16px' }}
                          disabled={isLoading || postText.length === 0}
                          disableElevation
                        >
                          Schedule Post
                        </LoadingButton>
                        <LoadingButton
                          loading={loadingAction === 'postNow'}
                          onClick={() => createSocialMediaPost('postNow')}
                          variant="contained"
                          disabled={isLoading || postText.length === 0}
                          sx={{ marginLeft: '16px' }}
                          disableElevation
                        >
                          Post Now
                        </LoadingButton>
                      </>
                    )}
                  </Box>
                </ToolbarSection>
              </CardContent>
            </Card>
          </DialogContent>
        </DialogContentWrapper>
      </Dialog>
      {effectiveLocationId && (
        <ImageLibraryDialog
          open={imageLibraryOpen}
          locationId={effectiveLocationId}
          onClose={() => setImageLibraryOpen(false)}
          onSelect={(imageUrl: string) => setAttachedImageUrl(imageUrl)}
        />
      )}
      {attachedImageUrl && (
        <PreviewImageDialog
          open={previewImageDialogOpen}
          url={attachedImageUrl}
          onClose={() => setPreviewImageDialogOpen(false)}
        />
      )}
    </>
  );
};

const ComposeSocialMediaPostWithProfile: FC<ComposeSocialMediaPostProps> = (props) => {
  const { contactDetails } = usePatientProfile();

  return (
    <ComposeSocialMediaPostImpl
      {...props}
      effectiveLocationId={props.locationId ?? contactDetails?.location?.id}
    />
  );
};

export const ComposeSocialMediaPost: FC<ComposeSocialMediaPostProps> = (props) => {
  if (props.locationId !== undefined) {
    return <ComposeSocialMediaPostImpl {...props} effectiveLocationId={props.locationId} />;
  }

  return <ComposeSocialMediaPostWithProfile {...props} />;
};
